// App shell: top bar (with the candy-stripe accent bar) plus the scrollable
// main content area. Auth gates the whole shell. There is no department
// sidebar (this app has one flat tile menu, not Cockpit's department nav),
// so the shell is simpler than Cockpit's: just Header + <Outlet>.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useServiceDeskUnread, type HelpCenterView } from '@ampersandbrands/ui';
import { Header } from './Header';
import { AuthGate } from './AuthGate';
import { BottomNav } from './BottomNav';
import { ChatBubble } from './ChatBubble';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { resolveRouteTitle } from './routeTitles';
import { HelpAndSupport } from '@/components/HelpAndSupport';
import { createServiceDeskClient } from '@/lib/serviceDeskClient';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { useSession } from '@/lib/useSession';
import { useEffectiveStoreNo } from '@/lib/useStores';

export function Layout() {
  // Keyed by pathname so navigating away from a crashed page resets the boundary.
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  useDocumentTitle(resolveRouteTitle(pathname));
  const [helpOpen, setHelpOpen] = useState(false);
  // Set when a link such as /?help_ticket=42 (the request-received email) asks to open one request.
  const [helpView, setHelpView] = useState<HelpCenterView | undefined>(undefined);

  // /?help_ticket=42 opens the Help Center on request 42, then drops the parameter so a refresh does not reopen it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = Number(params.get('help_ticket'));
    if (!Number.isInteger(id) || id <= 0) return;
    setHelpView({ ticket: id });
    setHelpOpen(true);
    params.delete('help_ticket');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [location.pathname, location.search, navigate]);
  const { user } = useSession();
  const storeNo = useEffectiveStoreNo();

  // One Service Desk client for the session. It reads the store a corporate
  // user picked in the header at call time, so switching stores needs no new
  // client.
  const storeNoRef = useRef(storeNo);
  storeNoRef.current = storeNo;
  const client = useMemo(() => createServiceDeskClient(() => storeNoRef.current), []);
  const { count: unread, refresh } = useServiceDeskUnread(client, { intervalMs: 60_000, enabled: !!user });

  return (
    <AuthGate>
      <div className="app-shell flex h-screen flex-col overflow-hidden bg-candy-cream text-candy-ink dark:bg-candy-dark-bg dark:text-candy-dark-ink">
        <Header
          onHelpOpen={() => {
            setHelpView(undefined);
            setHelpOpen(true);
          }}
          helpUnread={unread}
        />
        <main data-sweetspot-main className="main-with-bottom-nav min-w-0 flex-1 overflow-y-auto px-4 py-6 sm:px-8">
          <RouteErrorBoundary key={pathname}>
            <Outlet />
          </RouteErrorBoundary>
        </main>
        <HelpAndSupport
          open={helpOpen}
          onClose={() => {
            setHelpOpen(false);
            setHelpView(undefined);
          }}
          initialView={helpView}
          client={client}
          onActivity={() => void refresh()}
        />
        <ChatBubble />
        <BottomNav />
      </div>
    </AuthGate>
  );
}
